import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useOrganisation } from '../contexts/OrganisationContext';
import { Project } from '../types/api';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { ChartBarIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';

const OrganisationQuotasPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const {
    currentOrganisation,
    quotaUsage,
    selectOrganisation,
    refreshQuotaUsage,
  } = useOrganisation();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgId && (!currentOrganisation || currentOrganisation.id !== orgId)) {
      selectOrganisation(orgId).catch(() => {
        toast.error(t('quotas.loadOrgError'));
        navigate('/organisations');
      });
    }
  }, [orgId, currentOrganisation, selectOrganisation, navigate]);

  useEffect(() => {
    const loadProjects = async () => {
      if (!currentOrganisation) return;

      try {
        setLoading(true);
        const projectsData = await apiService.getProjects();
        setProjects(projectsData);
        await refreshQuotaUsage();
      } catch (error: unknown) {
        toast.error(t('quotas.loadError'));
      } finally {
        setLoading(false);
      }
    };

    if (currentOrganisation) {
      loadProjects();
    }
  }, [currentOrganisation, refreshQuotaUsage]);

  const getQuotaPercentageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-400';
    if (percentage >= 75) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getProgressBarColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading || !currentOrganisation || !quotaUsage) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('quotas.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/organisations/${orgId}/dashboard`)}
          className="text-sm text-accent-400 hover:text-accent-300 mb-2"
        >
          ← {t('quotas.backToDashboard')}
        </button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">
              {t('quotas.title')}
            </h1>
            <p className="mt-2 text-text-secondary">
              {t('quotas.subtitle', { name: currentOrganisation.nom, plan: currentOrganisation.plan_type })}
            </p>
          </div>
          <Link
            to={`/organisations/${orgId}/plan`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700"
          >
            <ArrowTrendingUpIcon className="h-5 w-5 mr-2" />
            {t('quotas.managePlan')}
          </Link>
        </div>
      </div>

      {/* Overall Quota Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* CPU */}
        <div className="bg-surface-2 shadow-card rounded-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-text-primary">{t('dashboard.quotas.cpu')}</h3>
            <ChartBarIcon className="h-6 w-6 text-text-quaternary" />
          </div>
          <div className="mb-4">
            <div className="flex items-baseline justify-between">
              <span className="text-3xl font-bold text-text-primary">
                {quotaUsage.cpu.used.toFixed(1)}
              </span>
              <span className="text-sm text-text-tertiary">
                / {quotaUsage.cpu.total} vCPU
              </span>
            </div>
            <p className={`text-sm font-medium mt-1 ${getQuotaPercentageColor(quotaUsage.cpu.percentage)}`}>
              {t('quotas.used', { percentage: quotaUsage.cpu.percentage.toFixed(1) })}
            </p>
          </div>
          <div className="w-full bg-surface-3 rounded-full h-3">
            <div
              className={`h-3 rounded-full ${getProgressBarColor(quotaUsage.cpu.percentage)}`}
              style={{ width: `${Math.min(quotaUsage.cpu.percentage, 100)}%` }}
            ></div>
          </div>
          <p className="mt-2 text-xs text-text-tertiary">
            {t('quotas.available', { value: quotaUsage.cpu.available.toFixed(1), unit: 'vCPU' })}
          </p>
        </div>

        {/* RAM */}
        <div className="bg-surface-2 shadow-card rounded-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-text-primary">{t('dashboard.quotas.ram')}</h3>
            <ChartBarIcon className="h-6 w-6 text-text-quaternary" />
          </div>
          <div className="mb-4">
            <div className="flex items-baseline justify-between">
              <span className="text-3xl font-bold text-text-primary">
                {quotaUsage.ram.used.toFixed(1)}
              </span>
              <span className="text-sm text-text-tertiary">
                / {quotaUsage.ram.total} GB
              </span>
            </div>
            <p className={`text-sm font-medium mt-1 ${getQuotaPercentageColor(quotaUsage.ram.percentage)}`}>
              {t('quotas.used', { percentage: quotaUsage.ram.percentage.toFixed(1) })}
            </p>
          </div>
          <div className="w-full bg-surface-3 rounded-full h-3">
            <div
              className={`h-3 rounded-full ${getProgressBarColor(quotaUsage.ram.percentage)}`}
              style={{ width: `${Math.min(quotaUsage.ram.percentage, 100)}%` }}
            ></div>
          </div>
          <p className="mt-2 text-xs text-text-tertiary">
            {t('quotas.available', { value: quotaUsage.ram.available.toFixed(1), unit: 'GB' })}
          </p>
        </div>

        {/* Storage */}
        <div className="bg-surface-2 shadow-card rounded-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-text-primary">{t('dashboard.quotas.storage')}</h3>
            <ChartBarIcon className="h-6 w-6 text-text-quaternary" />
          </div>
          <div className="mb-4">
            <div className="flex items-baseline justify-between">
              <span className="text-3xl font-bold text-text-primary">
                {quotaUsage.storage.used.toFixed(1)}
              </span>
              <span className="text-sm text-text-tertiary">
                / {quotaUsage.storage.total} GB
              </span>
            </div>
            <p className={`text-sm font-medium mt-1 ${getQuotaPercentageColor(quotaUsage.storage.percentage)}`}>
              {t('quotas.used', { percentage: quotaUsage.storage.percentage.toFixed(1) })}
            </p>
          </div>
          <div className="w-full bg-surface-3 rounded-full h-3">
            <div
              className={`h-3 rounded-full ${getProgressBarColor(quotaUsage.storage.percentage)}`}
              style={{ width: `${Math.min(quotaUsage.storage.percentage, 100)}%` }}
            ></div>
          </div>
          <p className="mt-2 text-xs text-text-tertiary">
            {t('quotas.available', { value: quotaUsage.storage.available.toFixed(1), unit: 'GB' })}
          </p>
        </div>
      </div>

      {/* Per-Project Breakdown */}
      <div className="bg-surface-2 shadow-card rounded-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border-1">
          <h2 className="text-lg font-medium text-text-primary">
            {t('quotas.perProject.title', { count: quotaUsage.project_count })}
          </h2>
        </div>

        {projects.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-text-tertiary">
              {t('quotas.perProject.empty')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border-1">
              <thead className="bg-surface-3">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    {t('quotas.perProject.tableProject')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    {t('quotas.perProject.tableCpu')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    {t('quotas.perProject.tableRam')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    {t('quotas.perProject.tableStorage')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    {t('quotas.perProject.tableCreated')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-2 divide-y divide-border-1">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-surface-3">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text-primary">
                        {project.nom}
                      </div>
                      {project.description && (
                        <div className="text-xs text-text-tertiary">
                          {project.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {project.quota_cpu}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {project.quota_ram}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {project.quota_stockage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                      {new Date(project.date_creation).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-surface-3">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                    {t('quotas.perProject.total')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-text-primary">
                    {quotaUsage.cpu.used.toFixed(1)} / {quotaUsage.cpu.total}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-text-primary">
                    {quotaUsage.ram.used.toFixed(1)} / {quotaUsage.ram.total}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-text-primary">
                    {quotaUsage.storage.used.toFixed(1)} / {quotaUsage.storage.total}
                  </td>
                  <td className="px-6 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Warning if approaching limits */}
      {(quotaUsage.cpu.percentage >= 75 || quotaUsage.ram.percentage >= 75 || quotaUsage.storage.percentage >= 75) && (
        <div className="mt-6 bg-yellow-500/12 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-400">
                {t('quotas.warning.approachingLimits')}{' '}
                <Link to={`/organisations/${orgId}/plan`} className="font-medium underline">
                  {t('quotas.warning.upgradePlan')}
                </Link>
                {' '}{t('quotas.warning.toContinue')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationQuotasPage;
